import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiFileText, FiPrinter
} from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import { getEmployeesCached, getJobsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { formatAmount } from '../../utils/documentNumbering';
import {
  createTransaction, updateTransaction, deleteTransaction
} from '../../utils/transactionHelper';
import { generateSubTransactions } from '../../utils/subTransactionHelper';
import PageLayout from '../../components/PageLayout';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import { isFutureDate, isLargeTransaction, LARGE_TRANSACTION_THRESHOLD, findTransactionsByReference } from '../../utils/validationHelpers';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import AttachmentPanel from '../../components/AttachmentPanel';
import DocListPagination from '../../components/DocListPagination';
import AccountSelect from '../../components/AccountSelect';
import InvoiceAllocationTable from '../../components/InvoiceAllocationTable';
import {
  getPendingInvoices, getInvoicesForEdit,
  applyInvoiceAllocations, reverseInvoiceAllocations
} from '../../utils/invoiceClearingHelper';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, LineSelect, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow,
  SortableTh, SortContext
} from '../../components/SharedStyles';
import { prewarmSave } from '../../utils/transactionHelper';

const TRAN_TYPE_CODE = 'JV';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted']
};

const EMPTY_LINE = { accountId: '', accountName: '', narration: '', debitAmount: 0, creditAmount: 0, linkedEntityId: null, linkedEntityType: null, invoiceAllocations: [] };

// A JV line "settles" a party when it reduces that party's balance:
//   customer receivable is normal-Dr → a CREDIT reduces it (like a receipt)
//   vendor payable is normal-Cr → a DEBIT reduces it (like a payment)
// Only in that direction can the line clear the party's open invoices/bills.
// Returns { partyType, partyId, amount } or null.
const getLineSettlement = (line) => {
  if (!line || !line.linkedEntityId) return null;
  const credit = Number(line.creditAmount) || 0;
  const debit = Number(line.debitAmount) || 0;
  if (line.linkedEntityType === 'customer' && credit > 0) {
    return { partyType: 'customer', partyId: line.linkedEntityId, amount: credit };
  }
  if (line.linkedEntityType === 'vendor' && debit > 0) {
    return { partyType: 'vendor', partyId: line.linkedEntityId, amount: debit };
  }
  return null;
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.success.main,
};

const GeneralJournal = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch } = useAuth();
  // Speed: prepare the first save in the background as soon as this screen opens.
  useEffect(() => {
    if (!activeCompany?.id || !activeFinancialYear?.id) return;
    prewarmSave(activeCompany.id, {
      tranTypeCode: TRAN_TYPE_CODE,
      branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
      financialYearId: activeFinancialYear.id,
    });
  }, [activeCompany?.id, activeFinancialYear?.id, activeBranch?.id]);
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'journalVoucher');
  const showBranchCol = activeBranch && activeBranch.id === 'all';
  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);

  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records,
    loading,
    refresh: loadRecords,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    voucherType: 'Journal', employeeId: '', employeeName: '', jobId: '', jobName: '',
    reference: '', narration: '', notes: '', status: 'Draft'
  });
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  // Pending invoices per settling line, keyed by line index: { key, invoices }.
  const [pendingByLine, setPendingByLine] = useState({});

  // Update the invoice allocations for one line.
  const setLineAllocations = (index, allocations) => {
    setLineItems(prev => prev.map((l, i) => i === index ? { ...l, invoiceAllocations: allocations } : l));
  };

  // Load pending invoices for each line that settles a party (customer credit /
  // vendor debit). Deduped per line+party, so typing an amount never re-fetches;
  // only a party (account) change does. Uses getInvoicesForEdit in edit mode so
  // already-cleared invoices reappear for re-allocation.
  useEffect(() => {
    if (!activeCompany?.id) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (let i = 0; i < lineItems.length; i++) {
        const s = getLineSettlement(lineItems[i]);
        if (!s) continue;
        const key = `${s.partyType}:${s.partyId}`;
        if (pendingByLine[i]?.key === key) continue; // already loaded for this party
        try {
          const invs = editingRecord
            ? await getInvoicesForEdit(activeCompany.id, s.partyType, s.partyId, lineItems[i].invoiceAllocations || [])
            : await getPendingInvoices(activeCompany.id, s.partyType, s.partyId);
          updates[i] = { key, invoices: invs };
        } catch (e) { console.error('Error loading pending invoices for JV settlement:', e); }
      }
      if (!cancelled && Object.keys(updates).length) {
        setPendingByLine(prev => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, activeCompany?.id]);

  // Record list now comes from useDocListPagination above (loadRecords = its
  // refresh). The old full-history getTransactions load lived here.

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  // NOTE: the record list itself loads inside useDocListPagination — not here.
  useEffect(() => { loadEmployees(); loadJobs(); }, [loadEmployees, loadJobs]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) || r.narration?.toLowerCase().includes(q) || r.voucherType?.toLowerCase().includes(q) || r.reference?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const totals = useMemo(() => {
    const totalDebit = Math.round(lineItems.reduce((s, l) => s + (Number(l.debitAmount) || 0), 0) * 100) / 100;
    const totalCredit = Math.round(lineItems.reduce((s, l) => s + (Number(l.creditAmount) || 0), 0) * 100) / 100;
    const difference = Math.round((totalDebit - totalCredit) * 100) / 100;
    return { totalDebit, totalCredit, difference };
  }, [lineItems]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Debit and Credit are mutually exclusive on a JV line — entering one
      // clears the other so the line never carries both.
      if (field === 'debitAmount' && Number(value) > 0) {
        updated[index].creditAmount = 0;
      }
      if (field === 'creditAmount' && Number(value) > 0) {
        updated[index].debitAmount = 0;
      }
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (index) => { if (lineItems.length <= 2) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({ docNumber: '', docDate: new Date().toISOString().split('T')[0], voucherType: 'Journal', employeeId: '', employeeName: '', jobId: '', jobName: '', reference: '', narration: '', notes: '', status: 'Draft' });
    setLineItems([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
    setPendingByLine({});
    setEditingRecord(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };
  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({ docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0], voucherType: rec.voucherType || 'Journal', employeeId: rec.employeeId || '', employeeName: rec.employeeName || '', jobId: rec.jobId || '', jobName: rec.jobName || '', reference: rec.reference || '', narration: rec.narration || '', notes: rec.notes || '', status: rec.status || 'Draft' });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
    setViewMode('form');
  };
  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => {
      if (rec) {
        handleView(rec);
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, ensureDoc]);

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.narration.trim()) { alert('Please enter a narration'); return; }
    const validLines = lineItems.filter(l => l.accountId && (Number(l.debitAmount) > 0 || Number(l.creditAmount) > 0));
    if (validLines.length < 2) { alert('Please add at least two journal entry lines with amounts'); return; }
    if (saveStatus === 'Posted') {
      const totalDebit = Math.round(validLines.reduce((s, l) => s + (Number(l.debitAmount) || 0), 0) * 100) / 100;
      const totalCredit = Math.round(validLines.reduce((s, l) => s + (Number(l.creditAmount) || 0), 0) * 100) / 100;
      if (totalDebit !== totalCredit) { alert('Total Debit must equal Total Credit to post the voucher'); return; }
      if (totalDebit <= 0) { alert('Total Debit must be greater than zero'); return; }
    }

    // Invoice-settlement guard: a line can never clear MORE than the amount it
    // actually moves, or the aging would drop below the ledger again.
    for (const l of validLines) {
      const s = getLineSettlement(l);
      if (!s) continue;
      const alloc = (l.invoiceAllocations || []).reduce((sum, a) => sum + (Number(a.allocatedAmount) || 0), 0);
      if (alloc > s.amount + 0.01) {
        alert(`"${l.accountName}" moves ${s.amount.toFixed(2)} but you allocated ${alloc.toFixed(2)} to invoices. Reduce the allocation to at most the line amount.`);
        return;
      }
    }

    // Duplicate-reference warning: catch a reference number already used on
    // another JV (a common accidental double-entry) but still let the user go
    // ahead — the same cheque/reference can legitimately span two vouchers.
    if (formData.reference && formData.reference.trim()) {
      try {
        const dupes = await findTransactionsByReference(
          activeCompany?.id, TRAN_TYPE_CODE, formData.reference, editingRecord?.id
        );
        if (dupes.length > 0) {
          const list = dupes.slice(0, 3).join(', ') + (dupes.length > 3 ? ', …' : '');
          if (!window.confirm(`Reference "${formData.reference.trim()}" is already used on ${dupes.length > 1 ? 'these JVs' : 'JV'}: ${list}.\n\nSave this voucher anyway?`)) return;
        }
      } catch (e) { /* lookup failure must not block a valid save */ }
    }

    // Financial year validation
    if (!activeFinancialYear?.id) {
      alert('No active financial year found. Please set an active financial year in Settings > Financial Year.');
      return;
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Large transaction warning
    if (isLargeTransaction(totals.totalDebit || 0)) {
      if (!window.confirm('This is a large transaction. Please verify the amount. Continue?')) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const s = getLineSettlement(l);
        return {
          ...l,
          debitAmount: Number(l.debitAmount) || 0,
          creditAmount: Number(l.creditAmount) || 0,
          // Keep allocations only on lines that actually settle a party; drop any
          // stale ones (e.g. account/direction changed after allocating).
          invoiceAllocations: s ? (l.invoiceAllocations || []).filter(a => Number(a.allocatedAmount) > 0) : [],
          lineNumber: i + 1
        };
      });
      const status = saveStatus || 'Draft';
      const totalDebit = Math.round(calculatedLines.reduce((s, l) => s + l.debitAmount, 0) * 100) / 100;
      const totalCredit = Math.round(calculatedLines.reduce((s, l) => s + l.creditAmount, 0) * 100) / 100;
      const data = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate, voucherType: formData.voucherType,
        branchId: activeBranch?.id || null, branchName: activeBranch?.branchName || null,
        employeeId: formData.employeeId, employeeName: formData.employeeName,
        jobId: formData.jobId, jobName: formData.jobName,
        reference: formData.reference, narration: formData.narration, notes: formData.notes, status,
        financialYearId: activeFinancialYear.id,
        totalDebit, totalCredit, lines: calculatedLines
      };

      let savedDocId = editingRecord?.id;
      let savedDocNumber = editingRecord?.docNumber;
      let newTranData = null;

      // Ledger lines for this voucher. On EDIT they go in with the update so the
      // ledger is replaced in the same save (a posted voucher re-saved as posted
      // used to lose its ledger lines).
      const jvManualLines = calculatedLines.map(l => ({
        accountId: l.accountId,
        accountCode: l.accountCode || '',
        accountName: l.accountName,
        debitAmount: l.debitAmount || 0,
        creditAmount: l.creditAmount || 0,
        description: l.narration || formData.narration
      }));
      if (editingRecord && status === 'Posted') data.subTransactionLines = jvManualLines;

      if (editingRecord) {
        await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
        savedDocNumber = editingRecord.docNumber;
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
        savedDocId = newTranData.id;
        savedDocNumber = newTranData.docNumber;
      }

      // Generate SubTransactions for a NEW posted voucher (edits are handled
      // inside updateTransaction above).
      if (!editingRecord && status === 'Posted') {

        // Generate new subTransactions with manual lines
        await generateSubTransactions(activeCompany.id, {
          transactionId: savedDocId,
          tranTypeCode: TRAN_TYPE_CODE,
          docNumber: savedDocNumber,
          docDate: formData.docDate,
          financialYearId: activeFinancialYear.id,
          branchId: activeBranch?.id || null,
          branchName: activeBranch?.branchName || null,
          status: 'Posted',
          manualLines: calculatedLines.map(l => ({
            accountId: l.accountId,
            accountCode: l.accountCode || '',
            accountName: l.accountName,
            debitAmount: l.debitAmount || 0,
            creditAmount: l.creditAmount || 0,
            description: l.narration || formData.narration
          }))
        }, currentUser.uid);
      }

      // Invoice settlement — clear (or re-clear) the party invoices/bills this
      // JV pays off, using the SAME helper as receipts/payments. On edit we
      // reverse the previous allocation first so re-saving recomputes cleanly;
      // allocations apply only when Posted, so a Draft frees the invoices.
      const flatAllocations = calculatedLines
        .flatMap(l => l.invoiceAllocations || [])
        .filter(a => a && a.allocatedAmount > 0);
      if (editingRecord) {
        await reverseInvoiceAllocations(activeCompany.id, savedDocId);
      }
      if (status === 'Posted' && flatAllocations.length > 0) {
        await applyInvoiceAllocations(activeCompany.id, flatAllocations, savedDocId);
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const appliedAllocations = status === 'Posted' ? flatAllocations : [];
      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: savedDocId, docNumber: savedDocNumber, status, invoiceAllocations: appliedAllocations }
        : { ...newTranData, status, invoiceAllocations: appliedAllocations };
      applyLocal(prev => editingRecord
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      loadRecords().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      // Free any invoices/bills this JV had cleared (mirrors receipt/payment delete).
      if (rec.invoiceAllocations && rec.invoiceAllocations.length > 0) {
        await reverseInvoiceAllocations(activeCompany.id, rec.id);
      }
      // ACC-04 fix (2026-09-30): deleteTransaction() runs the safety checks FIRST
      // (linked documents, receipts, FBR) and only then removes ledger + stock rows.
      // Wiping them here beforehand left a blocked document with no accounting.
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) { console.error('Error deleting:', error); alert('Error deleting record'); }
  };

  const handlePrint = (rec) => {
    const src = rec || viewingRecord;
    if (!src) return;
    // Shared voucher template renders the Dr/Cr lines (from data.lines), the
    // balance check, and the signature block per Print Format Settings.
    const data = {
      ...src,
      referenceNo: src.reference || src.referenceNo || '',
      narration: src.narration || src.notes || '',
    };
    printDocument('journalVoucher', data, activeCompany, printConfig, activeBranch);
  };

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search vouchers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Journal Voucher</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Journal Vouchers</h3>
          <p>{records.length === 0 ? 'Create your first journal voucher' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Voucher</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="voucherType">Type</SortableTh><SortableTh field="narration">Narration</SortableTh>
          <SortableTh field="totalDebit" style={{ textAlign: 'right' }}>Debit</SortableTh><SortableTh field="totalCredit" style={{ textAlign: 'right' }}>Credit</SortableTh>
          <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>
              {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
              <Td>{rec.voucherType}</Td><Td>{rec.narration || '-'}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalDebit)}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalCredit)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
              </ActionGroup></Td>
            </Tr>
          ))}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong> record(s)</span>
        <span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Posted: <strong>{listSummary.counts.Posted || 0}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>
        <FormCard>
          <SectionTitle>Voucher Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
            <InfoItem><label>Type</label><span>{rec.voucherType}</span></InfoItem>
            <InfoItem><label>Reference</label><span>{rec.reference || '-'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{rec.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{rec.jobName || '-'}</span></InfoItem>
            <InfoItem style={{ gridColumn: 'span 2' }}><label>Narration</label><span>{rec.narration || '-'}</span></InfoItem>
          </InfoGrid>
        </FormCard>
        <FormCard>
          <SectionTitle>Journal Entries</SectionTitle>
          <TableContainer><Table><thead><tr>
            <Th>#</Th><Th>Account</Th><Th>Narration</Th>
            <Th style={{ textAlign: 'right' }}>Debit (Dr)</Th><Th style={{ textAlign: 'right' }}>Credit (Cr)</Th>
          </tr></thead><tbody>
            {(rec.lines || []).map((line, i) => (
              <Tr key={i}><Td>{i + 1}</Td><Td>{line.accountName}</Td>
                <Td>{line.narration || '-'}</Td>
                <Td style={{ textAlign: 'right' }}>{line.debitAmount > 0 ? formatAmount(line.debitAmount) : '-'}</Td>
                <Td style={{ textAlign: 'right' }}>{line.creditAmount > 0 ? formatAmount(line.creditAmount) : '-'}</Td>
              </Tr>
            ))}
          </tbody></Table></TableContainer>
          <TotalsSection><TotalsTable><tbody>
            <tr><td>Total Debit ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalDebit)}</td></tr>
            <tr><td>Total Credit ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalCredit)}</td></tr>
            <TotalRow><td>Difference</td><td style={{ color: (rec.totalDebit - rec.totalCredit) !== 0 ? colors.error.main : undefined }}>{formatAmount(Math.round((rec.totalDebit - rec.totalCredit) * 100) / 100)}</td></TotalRow>
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
        {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
        <AttachmentPanel transactionId={rec?.id} attachment={rec?.attachment} canEdit={false} />
      </>
    );
  };

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Journal Voucher'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && !(editingRecord && editingRecord.status && editingRecord.status !== 'Draft' && editingRecord.status !== 'Cancelled') && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (formData.status === 'Draft' || !editingRecord) && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving || totals.difference !== 0}><FiCheck /> Save & Post</SaveButton>
          )}
          {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
          {canEdit && (editingRecord && editingRecord.status && editingRecord.status !== 'Draft' && editingRecord.status !== 'Cancelled') && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving || totals.difference !== 0}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
          )}
        </FormActions>
      </FormHeader>
      <FormCard>
        <SectionTitle>Voucher Details</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
          <FormGroup><Label>Voucher Type *</Label>
            <Select value={formData.voucherType} onChange={e => handleHeaderChange('voucherType', e.target.value)}>
              <option value="Journal">Journal</option><option value="Adjustment">Adjustment</option>
              <option value="Transfer">Transfer</option>
            </Select>
          </FormGroup>
          <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
          <FormGroup>
            <Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => {
              const emp = employees.find(em => em.id === e.target.value);
              handleHeaderChange('employeeId', e.target.value);
              handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
            }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Job</Label>
            <Select value={formData.jobId} onChange={e => {
              const job = jobs.find(j => j.id === e.target.value);
              handleHeaderChange('jobId', e.target.value);
              handleHeaderChange('jobName', job?.jobName || '');
            }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup><Label>Reference</Label><Input value={formData.reference} onChange={e => handleHeaderChange('reference', e.target.value)} placeholder="Reference number" /></FormGroup>
          <FormGroup $span2><Label>Narration *</Label><Input value={formData.narration} onChange={e => handleHeaderChange('narration', e.target.value)} placeholder="Narration / description of journal entry" /></FormGroup>
        </FormGrid>
      </FormCard>
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Journal Entries</SectionTitle>
          <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
          <LineTh style={{ width: 280, minWidth: 280, maxWidth: 280 }}>Account</LineTh>
          <LineTh style={{ width: 200, minWidth: 200, maxWidth: 200 }}>Narration</LineTh>
          <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Debit (Dr)</LineTh>
          <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Credit (Cr)</LineTh>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => (
            <tr key={index}>
              <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
              <LineTd>
                <AccountSelect
                  companyId={activeCompany?.id}
                  value={line.accountId}
                  onChange={(account) => {
                    // AccountSelect hands back the full account object — set
                    // id, code, and name in one shot so we don't depend on the
                    // local `accounts` cache (which may not include items the
                    // searchable picker just loaded).
                    setLineItems(prev => {
                      const updated = [...prev];
                      updated[index] = {
                        ...updated[index],
                        accountId: account?.id || '',
                        accountCode: account?.code || '',
                        accountName: account?.name || '',
                        // Party link — enables invoice settlement on this line.
                        linkedEntityId: account?.linkedEntityId || null,
                        linkedEntityType: account?.linkedEntityType || null,
                        // Account changed → any prior allocations belong to the
                        // old party, so drop them.
                        invoiceAllocations: []
                      };
                      return updated;
                    });
                  }}
                />
              </LineTd>
              <LineTd><LineInput value={line.narration} onChange={e => handleLineChange(index, 'narration', e.target.value)} placeholder="Line narration" /></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.debitAmount} onChange={e => handleLineChange(index, 'debitAmount', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.creditAmount} onChange={e => handleLineChange(index, 'creditAmount', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 2 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
            </tr>
          ))}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody>
          <tr><td>Total Debit ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalDebit)}</td></tr>
          <tr><td>Total Credit ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalCredit)}</td></tr>
          <TotalRow><td>Difference</td><td style={{ color: totals.difference !== 0 ? colors.error.main : undefined }}>{formatAmount(totals.difference)}</td></TotalRow>
        </tbody></TotalsTable></TotalsSection>
      </FormCard>
      {lineItems.some(l => getLineSettlement(l)) && (
        <FormCard>
          <SectionTitle>Settle Invoices (optional)</SectionTitle>
          <p style={{ fontSize: 12, color: colors.text.secondary, margin: '0 0 4px' }}>
            When a line reduces a customer's or vendor's balance, tick the invoices/bills it clears so the Aging report stays in step with the ledger. Allocations apply when you Post (and reverse if you edit or delete). Anything left unallocated stays on-account.
          </p>
          {lineItems.map((line, index) => {
            const s = getLineSettlement(line);
            if (!s) return null;
            return (
              <div key={index} style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.text.primary }}>
                  {line.accountName || 'Party account'} — {s.partyType === 'customer' ? 'Settle sales invoices' : 'Settle purchase bills'} · {activeCompany?.currencyCode || 'PKR'} {formatAmount(s.amount)}
                </div>
                <InvoiceAllocationTable
                  invoices={pendingByLine[index]?.invoices || []}
                  totalReceiptAmount={s.amount}
                  allocations={line.invoiceAllocations || []}
                  onAllocationsChange={(allocs) => setLineAllocations(index, allocs)}
                  currencyCode={activeCompany?.currencyCode || 'PKR'}
                />
              </div>
            );
          })}
        </FormCard>
      )}
      <FormCard>
        <SectionTitle>Notes</SectionTitle>
        <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid>
      </FormCard>

      <AttachmentPanel
        transactionId={editingRecord?.id}
        attachment={editingRecord?.attachment}
        canEdit={canEdit}
        onChange={({ attachment }) => setEditingRecord(prev => prev ? { ...prev, attachment } : prev)}
      />
    </>
  );

  useDocumentShortcuts({
    onNew: () => { if (!canEdit) { alert('You do not have permission to create.'); return; } handleCreate(); },
    onSave: () => { if (!canEdit) { alert('You do not have permission to save changes.'); return; } handleSave('Posted'); },
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="General Journal" breadcrumb="Journal / General Journal">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

// ============ UNIQUE STYLED COMPONENTS ============

export default GeneralJournal;
